import { FormikErrors } from 'formik';
import React, { useCallback, useState } from 'react';
import { ILandingPageBuilderForm, IResellerCustomerInfo } from '../../types';
import { Flex, Gap } from 'lib/components/styles/layout';
import { Divider } from '../form/styles';
import { ResellersDropdown } from 'app/pages/admin/customers/components/ResellersDropdown';
import styled, { useTheme } from 'styled-components/macro';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ParagraphSmall } from 'lib/components/styles/typography';
import { getResellerDetails } from 'lib/api';
import {
  DarkLabel,
  LabelSkeleton,
  ResellerAccordionBody,
  ResellerAccordionTitle,
} from './styles';
import { MdOutlineUnfoldLess, MdOutlineUnfoldMore } from 'react-icons/md';
import { FormikCheckboxField } from 'lib/components/formik/FormikCheckbox';
import CustomerAccess from './CustomerAccess';
import {
  IResellersWithCustomers,
  IResellersWithCustomersResponse,
} from 'lib/api/superadmin/useGetResellerCustomerCounts';
import {
  RefetchOptions,
  RefetchQueryFilters,
  QueryObserverResult,
  InfiniteData,
} from 'react-query';

const ResellerDropdownContainer = styled.div`
  width: 250px;
  margin-top: 13px;
`;

const INIT_SIZE = 50;

const INIT_RESELLER = {
  resellerId: 0,
  resellerName: '',
  customerCount: '0',
} as IResellersWithCustomers;

interface IResellersAndCustomersForm {
  resellers: IResellersWithCustomers[];
  count: number;
  refetch: <TPageData>(
    options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined
  ) => Promise<
    QueryObserverResult<InfiniteData<IResellersWithCustomersResponse>, unknown>
  >;
  isFetching: boolean;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  page: number;
  setResellersData: React.Dispatch<
    React.SetStateAction<IResellersWithCustomers[]>
  >;
  resellersData: IResellersWithCustomers[];
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean
  ) => Promise<void | FormikErrors<ILandingPageBuilderForm>>;

  values: {
    access: IResellerCustomerInfo;
    selectedResellerId: number;
  };
}

const ResellersAndCustomersForm = ({
  resellers,
  count,
  refetch,
  isFetching,
  setPage,
  page,
  setResellersData,
  resellersData,
  setFieldValue,
  values,
}: IResellersAndCustomersForm) => {
  const theme = useTheme();

  // reseller
  const [openReseller, setOpenReseller] =
    useState<IResellersWithCustomers>(INIT_RESELLER); // reseller currently open

  const getSingleReseller = async (resellerId: number): Promise<void> => {
    const reseller = await getResellerDetails(resellerId);
    setResellersData([reseller]);
    // set as open
    setOpenReseller(reseller);
  };

  const canResellerMenuOpen = (resellerId: number) => {
    const exists = values.access[resellerId];
    return !!exists && !!exists.totalCustomerCount;
  };

  const resetResellerSearch = () => {
    if (!page && !!resellers) {
      setResellersData(resellers);
      return;
    }
    // restart search
    setPage(0);
    refetch();
  };

  const toggleReseller = useCallback(
    (resellerId: number) => {
      const isChecked = !!values.access[resellerId]?.isResellerSelected;

      setFieldValue('access', {
        ...values.access,
        [resellerId]: {
          ...values.access[resellerId],
          isResellerSelected: !isChecked,
          selectedCustomers: !isChecked
            ? {}
            : values.access[resellerId].selectedCustomers,
        },
      });
    },
    [values.access, setFieldValue]
  );

  const isResellerChecked = useCallback(
    (resellerId: number) => {
      const resellerAccess = values.access[resellerId];
      return (
        !!resellerAccess &&
        !!resellerAccess.isResellerSelected &&
        !Object.keys(resellerAccess.selectedCustomers || {}).length
      );
    },
    [values.access]
  );

  const onChangeReseller = (resellerId: number) => {
    if (resellerId !== -1) {
      // find in reseller data, if not there, then refetch single reseller
      const reseller = resellersData.find(r => r.resellerId === resellerId);
      if (!!reseller) {
        setResellersData([reseller]);
        setOpenReseller(reseller);
      } else {
        getSingleReseller(resellerId);
      }
    } else {
      resetResellerSearch();
    }
    setFieldValue('selectedResellerId', resellerId);
  };

  const CustomerCount = ({ resellerId }: { resellerId: number }) => {
    if (isFetching) {
      return <LabelSkeleton style={{ width: '50px' }} />;
    }

    const exists = values.access[resellerId];
    const isChecked = isResellerChecked(resellerId);
    let dataToRender = '';

    if (exists && isChecked) {
      dataToRender = `${exists?.totalCustomerCount || 0}/${exists?.totalCustomerCount || 0}`;
    } else {
      const selectedCustomersCount = Object.keys(
        exists?.selectedCustomers || {}
      ).length;
      dataToRender = `${selectedCustomersCount || 0}/${exists?.totalCustomerCount || 0}`;
    }

    return (
      <ParagraphSmall
        style={{
          color: theme.colors.neutral[60],
        }}
      >
        {dataToRender}
      </ParagraphSmall>
    );
  };

  return (
    <Gap flexDirection='column' alignItems='flex-start'>
      <ResellerDropdownContainer>
        <DarkLabel style={{ marginBottom: '8px' }}>Select Reseller</DarkLabel>
        <ResellersDropdown
          onSelectReseller={resellerId => {
            onChangeReseller(resellerId);
          }}
          showAllResellerOption
          showOnlyActiveResellers
          menuPortalTarget={null}
        />
      </ResellerDropdownContainer>

      <InfiniteScroll
        dataLength={resellersData.length}
        hasMore={
          isFetching ||
          (values.selectedResellerId === -1
            ? count > resellersData.length
            : false)
        }
        loader={isFetching ? <h4>Loading...</h4> : <></>}
        next={() => {
          setPage(page + INIT_SIZE);
        }}
        height='60vh'
        endMessage={
          !!count ? (
            <></>
          ) : (
            <ParagraphSmall textAlign='center' style={{ marginTop: '110px' }}>
              There is no resellers that match this criteria
            </ParagraphSmall>
          )
        }
      >
        <Gap flexDirection='column' width='790px' gap='0'>
          {resellersData.map((reseller: IResellersWithCustomers) => {
            return (
              <Flex key={`reseller-customer-${reseller.resellerId}`}>
                <Divider />
                <ResellerAccordionTitle>
                  <Flex flexDirection='row' justifyContent='space-between'>
                    <Gap
                      gap='8px'
                      alignItems='center'
                      style={{
                        cursor: canResellerMenuOpen(reseller.resellerId)
                          ? 'pointer'
                          : 'not-allowed',
                      }}
                      onClick={() => {
                        if (!canResellerMenuOpen(reseller.resellerId)) return;
                        setOpenReseller(
                          openReseller.resellerId === reseller.resellerId
                            ? INIT_RESELLER
                            : reseller
                        );
                      }}
                    >
                      {openReseller.resellerId === reseller.resellerId &&
                      canResellerMenuOpen(reseller.resellerId) ? (
                        <MdOutlineUnfoldLess />
                      ) : (
                        <MdOutlineUnfoldMore />
                      )}
                      {reseller.resellerName}
                      <CustomerCount resellerId={reseller.resellerId} />
                    </Gap>

                    <Gap gap='12px' alignItems='center'>
                      <FormikCheckboxField
                        name={`resellers-${reseller.resellerId}`}
                        checked={isResellerChecked(reseller.resellerId)}
                        onChange={e => {
                          e.stopPropagation();
                          toggleReseller(reseller.resellerId);
                        }}
                        checkGroupIndicator
                        extendStyles={{
                          wrapper: {
                            flexDirection: 'row-reverse',
                            gap: '5px',
                          },
                          labelStyles: {
                            color: theme.colors.neutral[80],
                            fontSize: '14px',
                            fontWeight: 600,
                            marginRight: '5px',
                          },
                        }}
                        label='Share with Reseller'
                      />
                    </Gap>
                  </Flex>
                </ResellerAccordionTitle>

                {reseller.resellerId === openReseller.resellerId && (
                  <ResellerAccordionBody
                    isOpen={canResellerMenuOpen(openReseller.resellerId)}
                  >
                    <CustomerAccess
                      resellerId={openReseller.resellerId}
                      isShareWithResellerSelected={isResellerChecked(
                        openReseller.resellerId
                      )}
                      onToggleResellerFlag={() =>
                        toggleReseller(openReseller.resellerId)
                      }
                    />
                  </ResellerAccordionBody>
                )}
              </Flex>
            );
          })}
        </Gap>
      </InfiniteScroll>
    </Gap>
  );
};

export default React.memo(ResellersAndCustomersForm);
